import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { TextField, InputAdornment } from "@mui/material";
import { Button, Notifications, Spinner, Text } from "@jds/core";
import { v4 as uuidv4 } from "uuid";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import logo from "../../assets/png/JioCloud-Logo.png";
import useApiHttp from "../../hooks/use-http";
import { validatePhoneNumber } from "../../util/validation";
import useInput from "../../hooks/use-input";
import decryptUrl from "../../util/img-decryption";
import Otp from "../Auth/Otp";
import styles from "./UserRegistrationEntry.module.css";
import { addParamsURL } from "../../util/helper";

const sanitizePhoneNumber = (value) => {
  const digits = value.replace(/\D/g, "");
  if (digits.length > 10 && digits.startsWith("91")) return digits.slice(-10);
  return digits.replace(/^0+/, "").slice(0, 10);
};

export default function UserRegistrationEntry() {
  const location = useLocation();
  const eventId = new URLSearchParams(location.search).get("event_id") || "";
  const [event, setEvent] = useState(null);
  const [authData, setAuthData] = useState(null);
  const [deviceKey, setDeviceKey] = useState("");
  const { sendRequest: getEventDetails, isLoading: eventLoading } = useApiHttp();
  const { sendRequest: sendOtp, isLoading: otpLoading } = useApiHttp();
  const {
    value: phoneNumber,
    isValid,
    hasError,
    valueChangeHandler,
    inputBlurHandler,
  } = useInput(validatePhoneNumber);

  useEffect(() => {
    setDeviceKey(uuidv4());
    if (!eventId) return;
    getEventDetails(
      {
        url: `event/get-event-banner/${eventId}`, method: "GET",
      },
      null,
      (response) => {
        setEvent(response?.data || null);
        localStorage.bannerThumbnailURl = response?.data?.bannerThumbnailURl;
      },
      () => setEvent(null),
    );
  }, [eventId]);

  const handlePhoneChange = (e) => {
    valueChangeHandler({ target: { type: "number", value: sanitizePhoneNumber(e.target.value) } });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (!eventId || !isValid) return;
    sendOtp(
      {
        base: "JCAPI",
        isBearer: false,
        url: "account/jioid/sendotp",
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=UTF-8",
          "X-Api-Key": process.env.REACT_APP_APIKEY,
          "X-App-Secret": process.env.REACT_APP_APPSECRET,
          "X-Client-Details": process.env.REACT_APP_CLIENTDETAILS,
          "X-Device-Type": process.env.REACT_APP_DEVICETYPE,
        },
        body: { mobileNumber: `+91${phoneNumber}` },
      },
      null,
      (response) => {
        setAuthData({ channel: "mobile", phoneNumber, countryCode: "+91", identifier: response?.data?.identifier });
        Notifications.toast.success({ id: "registration-login", title: "OTP sent" });
      },
      (_, response) => Notifications.toast.error({ id: "registration-login", title: response?.error || response?.message || "Unable to send OTP" }),
    );
  };

  if (authData) {
    return (
      <main className={styles.page}>
        <header className={styles.header}>
          <div className={styles.headerContainer}><img src={logo} alt="JioAICloud" /></div>
        </header>
        <section className={styles.otpWrap}>
          <Otp
            authData={authData}
            deviceKey={deviceKey}
            registrationEventId={eventId}
            onBack={() => setAuthData(null)}
            handleSubmit={handleSubmit}
          />
        </section>
      </main>
    );
  }

  const thumbnail = event?.bannerThumbnailURl ? decryptUrl(event.bannerThumbnailURl) : "";

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContainer}><img src={logo} alt="JioAICloud" /></div>
      </header>
      <section className={styles.content}>
        <div className={styles.card}>
          <div className={styles.banner}>
            {thumbnail && <img src={addParamsURL(thumbnail, {
              appSecret: process.env.REACT_APP_APPSECRET,
            })} alt={`${event?.eventName || "Event"} banner`} />}
          </div>
          <form className={styles.form} onSubmit={handleSubmit}>
            <Text appearance="heading-xs" text={event?.eventName || event?.name || ""} textAlign="center" />
            {eventLoading ? (
              <div className={styles.loading}><Spinner size="small" label="Loading event" /></div>
            ) : !eventId ? (
              <p className={styles.error}>Event details are unavailable.</p>
            ) : (
              <>
                <div className={styles.phoneField}>
                  <label htmlFor="registration-mobile">Mobile number</label>
                  <TextField
                    id="registration-mobile"
                    variant="standard"
                    fullWidth
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    onBlur={inputBlurHandler}
                    error={hasError}
                    placeholder="Enter mobile number"
                    inputProps={{ inputMode: "numeric", maxLength: 10 }}
                    helperText={hasError ? "Enter a valid 10-digit mobile number" : " "}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <span className={styles.countryCode}>+91 <KeyboardArrowDownIcon fontSize="small" /></span>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiInput-root": { minHeight: 42, fontFamily: "var(--font-medium)", color: "#141414", fontSize: 18 },
                      "& .MuiInput-root:before": { borderBottomColor: "#222" },
                    }}
                  />
                </div>
                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  disabled={!isValid || otpLoading}
                  kind={otpLoading ? "secondary" : "primary"}
                  label="Register"
                >
                  {otpLoading ? <Spinner kind="vibrant" size="small" label="Sending OTP" /> : "Register"}
                </Button>
              </>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}
